import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve persistent visitor ID
const getVisitorId = () => {
  let vid = localStorage.getItem("rently_vid");
  if (!vid) {
    vid = "v_" + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem("rently_vid", vid);
  }
  return vid;
};

// Generate session ID (new per tab/session)
const getSessionId = () => {
  let sid = sessionStorage.getItem("rently_sid");
  if (!sid) {
    sid = "s_" + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem("rently_sid", sid);
  }
  return sid;
};

// Get UTM params from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
};

// Send tracking data (fire-and-forget with beacon fallback)
const sendTrack = async (endpoint, data) => {
  try {
    const url = `${BACKEND_URL}/api/track/${endpoint}`;
    // Use sendBeacon for reliability (survives page unload)
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Silent fail - tracking should never break the app
  }
};

// Event buffer for batching
let eventBuffer = [];
let flushTimeout = null;

const bufferEvent = (event) => {
  eventBuffer.push(event);
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(flushEvents, 3000); // Flush every 3s
};

const flushEvents = () => {
  if (eventBuffer.length === 0) return;
  const events = [...eventBuffer];
  eventBuffer = [];
  sendTrack("batch", events);
};

export const VisitorTracker = () => {
  const location = useLocation();
  const sessionInitialized = useRef(false);
  const lastPage = useRef("");
  const pageStartTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const visitorId = useRef(getVisitorId());
  const sessionId = useRef(getSessionId());

  // Initialize session on first load
  useEffect(() => {
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;

    const utm = getUTMParams();

    sendTrack("session", {
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      user_agent: navigator.userAgent,
      referrer: document.referrer || "",
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      landing_page: window.location.pathname,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language || "",
      timezone_offset: new Date().getTimezoneOffset(),
    });
  }, []);

  // Track page views on route change
  useEffect(() => {
    const page = location.pathname;
    if (page === lastPage.current) return;

    // Record time on previous page
    if (lastPage.current) {
      const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
      bufferEvent({
        session_id: sessionId.current,
        visitor_id: visitorId.current,
        event_type: "page_exit",
        page: lastPage.current,
        data: { time_seconds: timeSpent, max_scroll: maxScroll.current },
      });
    }

    lastPage.current = page;
    pageStartTime.current = Date.now();
    maxScroll.current = 0;

    // Track page view
    bufferEvent({
      session_id: sessionId.current,
      visitor_id: visitorId.current,
      event_type: "page_view",
      page: page,
      data: { title: document.title },
    });
  }, [location.pathname]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScroll.current) {
        maxScroll.current = scrollPercent;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track CTA clicks
  useEffect(() => {
    const handleClick = (e) => {
      const target = e.target.closest("[data-testid], a, button");
      if (!target) return;

      const testId = target.getAttribute("data-testid");
      const href = target.getAttribute("href");
      const text = target.textContent?.trim().substring(0, 80);

      // Only track meaningful clicks (buttons, CTAs, links)
      if (testId || href) {
        bufferEvent({
          session_id: sessionId.current,
          visitor_id: visitorId.current,
          event_type: "click",
          page: location.pathname,
          element: testId || href || "",
          data: { text: text, href: href || "" },
        });
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname]);

  // Track form interactions
  useEffect(() => {
    const trackedForms = new Set();

    const handleFocus = (e) => {
      const form = e.target.closest("form");
      if (!form) return;
      const formId = form.getAttribute("data-testid") || "unknown-form";

      if (!trackedForms.has(formId)) {
        trackedForms.add(formId);
        bufferEvent({
          session_id: sessionId.current,
          visitor_id: visitorId.current,
          event_type: "form_start",
          page: location.pathname,
          element: formId,
          data: { field: e.target.name || e.target.id || "" },
        });
      }

      bufferEvent({
        session_id: sessionId.current,
        visitor_id: visitorId.current,
        event_type: "form_field",
        page: location.pathname,
        element: formId,
        data: { field: e.target.name || e.target.id || "" },
      });
    };

    const handleSubmit = (e) => {
      const form = e.target.closest("form") || e.target;
      const formId = form.getAttribute("data-testid") || "unknown-form";

      // Flush immediately on form submit
      bufferEvent({
        session_id: sessionId.current,
        visitor_id: visitorId.current,
        event_type: "form_submit",
        page: location.pathname,
        element: formId,
        data: {},
      });
      flushEvents();
    };

    document.addEventListener("focusin", handleFocus, true);
    document.addEventListener("submit", handleSubmit, true);

    return () => {
      document.removeEventListener("focusin", handleFocus, true);
      document.removeEventListener("submit", handleSubmit, true);
    };
  }, [location.pathname]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
      eventBuffer.push({
        session_id: sessionId.current,
        visitor_id: visitorId.current,
        event_type: "page_exit",
        page: lastPage.current,
        data: { time_seconds: timeSpent, max_scroll: maxScroll.current },
      });
      flushEvents();
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  return null; // Invisible component
};

export default VisitorTracker;
